import { useState, useMemo } from 'react';
import { box, input, label, btnPlain, Question, Pick, PickMany } from './surveyFormParts';
import { Extras } from './DeskSurveySections';
import {
  MOVE_NETWORKS, MOVE_KIT, MOVE_STATUS_LABELS, MOVE_STATUS_TONES, PRIORITY_LABELS, MOVE_TYPE_LABELS, CHECK_LABELS, PHASES,
  YES_NO_LABELS, ISSUE_STATUS_LABELS, RISK_STATUS_LABELS, MOVE_OUTCOME_LABELS, moveRef, moveView, moveTotals, mapOf,
} from '../../utils/siteSurveys';

// The desk move's own sections (migration 0410), inside SurveyForm, following
// the team's Desk Moves workbook: one card per move, the summary its Move
// Summary tab gave, the equipment and issues register and the risks. The card
// adds what the workbook did not hold: each network's port at the old desk and
// the new one, whether it is live, and monitor arms.
//
// Before review everything is open. Once the survey is reviewed (`dayOpen`),
// only the move-day fields stay open, for ticking the moves off on the day.

const NETWORK_OPTIONS = Object.fromEntries(MOVE_NETWORKS.map(n => [n, n]));
const PHASE_OPTIONS = Object.fromEntries(PHASES.map(p => [String(p), `Wave ${p}`]));

function Field({ text, children, wide }) {
  return <div className={wide ? 'col-span-2' : ''}><div className={label}>{text}</div>{children}</div>;
}

function MoveCard({ move, i, update, remove, copyAbove, locked, dayOpen, open, toggle }) {
  const v = moveView(move);
  // Blocked already shows as the status, so the tags are the rest.
  const tags = v.issues.filter(x => !x.startsWith('blocked'));
  const set = (patch) => update(i, patch);
  const survey = locked; // the survey's own answers
  const day = locked && !dayOpen; // the move-day answers
  const ports = mapOf(move.ports_json);
  const setPort = (end, net, key, value) => {
    const atEnd = { ...mapOf(ports[end]) };
    atEnd[net] = { ...mapOf(atEnd[net]), [key]: value };
    set({ ports_json: { ...ports, [end]: atEnd } });
  };
  const kit = mapOf(move.kit_json);
  const where = (desk, area) => [desk, area].filter(Boolean).join(', ') || '?';

  return (
    <div className="border border-gray-200 rounded mb-1.5 bg-white">
      <button type="button" onClick={toggle} aria-expanded={open} className="w-full flex flex-wrap items-center gap-2 px-2 py-1.5 text-left hover:bg-gray-50">
        <span className="font-mono font-bold text-navy text-[11px] shrink-0">{move.move_ref}</span>
        <span className="text-[10px] text-gray-700 min-w-0 truncate">{move.user_name || move.team || 'No user yet'}</span>
        <span className="text-[10px] text-gray-500 min-w-0 truncate">{where(move.from_desk, move.from_area)} → {where(move.to_desk, move.to_area)}</span>
        <span className="flex flex-wrap gap-1 ml-auto">
          <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${MOVE_STATUS_TONES[move.status || 'not_started']}`}>{MOVE_STATUS_LABELS[move.status || 'not_started']}</span>
          {tags.slice(0, 2).map(x => <span key={x} className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-red-50 text-red-700">{x}</span>)}
          {tags.length > 2 && <span className="text-[8px] text-red-700">+{tags.length - 2} more</span>}
          <span className="text-gray-400 text-[10px]">{open ? '▲' : '▼'}</span>
        </span>
      </button>
      {open && (
        <div className="px-2 pb-2 pt-1 border-t border-gray-100 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Field text="User"><input className={input} value={move.user_name || ''} disabled={survey} onChange={e => set({ user_name: e.target.value })} /></Field>
            <Field text="Team"><input className={input} value={move.team || ''} disabled={survey} onChange={e => set({ team: e.target.value })} /></Field>
            <Field text="Move owner"><input className={input} value={move.move_owner || ''} disabled={survey} onChange={e => set({ move_owner: e.target.value })} /></Field>
            <Field text="Asset / label ref"><input className={input} value={move.asset_ref || ''} disabled={survey} onChange={e => set({ asset_ref: e.target.value })} /></Field>
            <Field text="From desk"><input className={input} value={move.from_desk || ''} disabled={survey} onChange={e => set({ from_desk: e.target.value })} /></Field>
            <Field text="From area"><input className={input} value={move.from_area || ''} disabled={survey} onChange={e => set({ from_area: e.target.value })} /></Field>
            <Field text="To desk"><input className={input} value={move.to_desk || ''} disabled={survey} onChange={e => set({ to_desk: e.target.value })} /></Field>
            <Field text="To area"><input className={input} value={move.to_area || ''} disabled={survey} onChange={e => set({ to_area: e.target.value })} /></Field>
            <Field text="Move date"><input type="date" className={input} value={move.move_date || ''} disabled={survey} onChange={e => set({ move_date: e.target.value || null })} /></Field>
            <Field text="Phase" wide><Pick options={PHASE_OPTIONS} value={move.phase ? String(move.phase) : null} disabled={survey} onChange={x => set({ phase: x ? Number(x) : null })} /></Field>
            <Field text="Priority" wide><Pick options={PRIORITY_LABELS} value={move.priority} disabled={survey} onChange={x => set({ priority: x })} /></Field>
            <Field text="Move type" wide><Pick options={MOVE_TYPE_LABELS} value={move.move_type} disabled={survey} onChange={x => set({ move_type: x })} /></Field>
          </div>

          <div>
            <div className="text-[10px] font-bold text-navy mb-1">Ready at the new desk?</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Field text="Destination"><Pick options={CHECK_LABELS} value={move.dest_ready} disabled={survey} onChange={x => set({ dest_ready: x })} /></Field>
              <Field text="Network / ports"><Pick options={CHECK_LABELS} value={move.network_ready} disabled={survey} onChange={x => set({ network_ready: x })} /></Field>
              <Field text="Power"><Pick options={CHECK_LABELS} value={move.power_ready} disabled={survey} onChange={x => set({ power_ready: x })} /></Field>
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold text-navy mb-1">Networks and ports</div>
            <Field text="Networks the user needs"><PickMany options={NETWORK_OPTIONS} value={move.networks} disabled={survey} onChange={x => set({ networks: x })} /></Field>
            <div className="overflow-x-auto mt-1.5">
              <table className="text-[10px]">
                <thead><tr className="text-[8px] uppercase text-gray-400"><th className="text-left pr-2">Network</th><th className="text-left pr-2">Old desk port</th><th className="text-left pr-2">Live?</th><th className="text-left pr-2">New desk port</th><th className="text-left">Live?</th></tr></thead>
                <tbody>
                  {MOVE_NETWORKS.map(net => (
                    <tr key={net} className={v.networks.includes(net) ? '' : 'opacity-60'}>
                      <td className="pr-2 py-0.5 font-semibold text-gray-700">{net}{v.networks.includes(net) ? '' : ' (not needed)'}</td>
                      <td className="pr-2 py-0.5"><input className={`${box} w-28`} value={v.at('from', net).port || ''} placeholder="As labelled" aria-label={`${net} port at the old desk`} disabled={survey} onChange={e => setPort('from', net, 'port', e.target.value)} /></td>
                      <td className="pr-2 py-0.5"><Pick options={CHECK_LABELS} value={v.at('from', net).live || null} disabled={survey} onChange={x => setPort('from', net, 'live', x)} /></td>
                      <td className="pr-2 py-0.5"><input className={`${box} w-28`} value={v.at('to', net).port || ''} placeholder="As labelled" aria-label={`${net} port at the new desk`} disabled={survey} onChange={e => setPort('to', net, 'port', e.target.value)} /></td>
                      <td className="py-0.5"><Pick options={CHECK_LABELS} value={v.at('to', net).live || null} disabled={survey} onChange={x => setPort('to', net, 'live', x)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold text-navy mb-1">Kit that moves</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
              <Field text="Monitors"><Pick options={YES_NO_LABELS} value={move.monitors} disabled={survey} onChange={x => set({ monitors: x })} /></Field>
              <Field text="How many"><input className={`${box} w-16`} inputMode="numeric" value={move.monitor_qty ?? ''} disabled={survey} onChange={e => set({ monitor_qty: e.target.value })} /></Field>
              <Field text="Monitor arms"><Pick options={YES_NO_LABELS} value={move.monitor_arms} disabled={survey} onChange={x => set({ monitor_arms: x })} /></Field>
              <Field text="How many"><input className={`${box} w-16`} inputMode="numeric" value={move.monitor_arm_qty ?? ''} disabled={survey} onChange={e => set({ monitor_arm_qty: e.target.value })} /></Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
              {MOVE_KIT.map(k => (
                <div key={k} className="flex items-center justify-between gap-2">
                  <span className="text-[10px] text-gray-700">{k}</span>
                  <Pick options={YES_NO_LABELS} value={kit[k] || null} disabled={survey} onChange={x => set({ kit_json: { ...kit, [k]: x || undefined } })} />
                </div>
              ))}
            </div>
          </div>

          <Field text="Notes"><input className={input} value={move.notes || ''} disabled={survey} onChange={e => set({ notes: e.target.value })} /></Field>

          <div className="border-t border-gray-100 pt-2">
            <div className="text-[10px] font-bold text-navy mb-1">Move day</div>
            <Field text="Status"><Pick options={MOVE_STATUS_LABELS} value={move.status} disabled={day} onChange={x => set({ status: x })} /></Field>
            {move.status === 'blocked' && (
              <div className="mt-1.5"><Field text="What is blocking it?"><input className={input} value={move.blocker || ''} disabled={day} onChange={e => set({ blocker: e.target.value })} /></Field></div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1.5">
              <Field text="Move complete"><Pick options={CHECK_LABELS} value={move.move_complete} disabled={day} onChange={x => set({ move_complete: x })} /></Field>
              <Field text="User check complete"><Pick options={CHECK_LABELS} value={move.user_check} disabled={day} onChange={x => set({ user_check: x })} /></Field>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1.5">
              <Field text="Packed by"><input className={input} value={move.packed_by || ''} disabled={day} onChange={e => set({ packed_by: e.target.value })} /></Field>
              <Field text="Collated date"><input type="date" className={input} value={move.collated_date || ''} disabled={day} onChange={e => set({ collated_date: e.target.value || null })} /></Field>
              <Field text="Storage / staging"><input className={input} value={move.staging_location || ''} disabled={day} onChange={e => set({ staging_location: e.target.value })} /></Field>
              <Field text="Kit status"><input className={input} value={move.kit_status || ''} disabled={day} onChange={e => set({ kit_status: e.target.value })} /></Field>
            </div>
            <div className="mt-1.5"><Field text="Missing kit or issue"><input className={input} value={move.missing_kit || ''} disabled={day} onChange={e => set({ missing_kit: e.target.value })} /></Field></div>
          </div>

          {!survey && (
            <div className="flex gap-3">
              {i > 0 && <button type="button" onClick={() => copyAbove(i)} className="text-[10px] font-semibold text-gold hover:underline">Same as the move above</button>}
              <button type="button" onClick={() => remove(i)} className="text-[10px] font-semibold text-red-600 hover:underline ml-auto">Remove {move.move_ref}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Moves({ parts, setParts, locked, dayOpen }) {
  const [adding, setAdding] = useState('1');
  const [openAt, setOpenAt] = useState(null);
  const [filter, setFilter] = useState('all');
  const moves = parts.moves;
  const update = (i, patch) => setParts({ ...parts, moves: moves.map((m, j) => (j === i ? { ...m, ...patch } : m)) });
  const remove = (i) => setParts({ ...parts, moves: moves.filter((_, j) => j !== i) });
  // What a run of moves for one team usually shares; who moves and which desks stay each move's own.
  const copyAbove = (i) => {
    const a = moves[i - 1];
    update(i, { team: a.team, from_area: a.from_area, to_area: a.to_area, move_date: a.move_date, phase: a.phase, priority: a.priority,
      move_type: a.move_type, move_owner: a.move_owner, networks: a.networks, monitors: a.monitors, monitor_qty: a.monitor_qty,
      monitor_arms: a.monitor_arms, monitor_arm_qty: a.monitor_arm_qty, kit_json: a.kit_json });
  };
  const add = () => {
    const n = Math.min(Math.max(Number(adding) || 0, 0), 200);
    if (!n) return;
    const top = moves.reduce((max, m) => Math.max(max, Number(String(m.move_ref || '').replace(/\D/g, '')) || 0), 0);
    setParts({ ...parts, moves: [...moves, ...Array.from({ length: n }, (_, k) => ({ move_ref: moveRef(top + k + 1) }))] });
  };
  const shown = moves.map((m, i) => [m, i]).filter(([m]) => {
    if (filter === 'all') return true;
    if (filter === 'issues') return moveView(m).issues.length > 0;
    if (filter === 'blocked') return m.status === 'blocked';
    return m.status !== 'complete';
  });

  return (
    <Question title={`Moves (${moves.length})`} hint="One card per person, desk, team or piece of equipment that moves. Open a card to record it. A move with no user, team or desk is not saved." tour="survey-moves">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {[['all', 'All'], ['issues', 'Not ready'], ['blocked', 'Blocked'], ['open', 'Not complete']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setFilter(k)} className={`px-2.5 py-1 text-[9px] font-semibold rounded-full border ${filter === k ? 'bg-navy text-white border-navy' : 'bg-white text-gray-500 border-gray-200'}`}>{l}</button>
        ))}
      </div>
      {shown.map(([m, i]) => (
        <MoveCard key={m.id || `${m.move_ref}-${i}`} move={m} i={i} update={update} remove={remove} copyAbove={copyAbove}
          locked={locked} dayOpen={dayOpen} open={openAt === i} toggle={() => setOpenAt(k => (k === i ? null : i))} />
      ))}
      {moves.length > 0 && shown.length === 0 && <div className="text-[10px] text-gray-400">No moves match.</div>}
      {!locked && (
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <input className={`${box} w-16`} inputMode="numeric" value={adding} onChange={e => setAdding(e.target.value)} aria-label="How many moves to add" />
          <button type="button" onClick={add} className={btnPlain}>Add moves</button>
        </div>
      )}
    </Question>
  );
}

function Summary({ parts }) {
  const t = useMemo(() => moveTotals(parts.moves, parts.extras), [parts]);
  if (!parts.moves.length) return null;
  const tile = (l, v, warn) => (
    <div key={l} className="border border-gray-100 rounded px-2 py-1.5">
      <div className="text-[8px] uppercase text-gray-400 font-semibold">{l}</div>
      <div className={`text-[14px] font-extrabold ${warn ? 'text-red-600' : 'text-navy'}`}>{v}</div>
    </div>
  );
  return (
    <Question title="Summary" hint="Worked out from the moves, as the workbook's Move Summary tab did." tour="survey-move-summary">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
        {tile('Planned moves', t.planned)}
        {tile('Ready', t.byStatus.ready)}
        {tile('Blocked', t.byStatus.blocked, t.byStatus.blocked > 0)}
        {tile('Complete', `${t.byStatus.complete} (${t.completion}%)`)}
        {tile('Destination not ready', t.notReady.dest, t.notReady.dest > 0)}
        {tile('Network not ready', t.notReady.network, t.notReady.network > 0)}
        {tile('Power not ready', t.notReady.power, t.notReady.power > 0)}
        {tile('User checks done', t.userChecks)}
        {tile('Monitors', t.monitors)}
        {tile('Monitor arms', t.monitorArms)}
        {tile('Issues open', t.openIssues, t.openIssues > 0)}
        {tile('Red or amber risks', t.redAmber, t.redAmber > 0)}
      </div>
      <div className="text-[10px] text-gray-600 mt-2">
        By wave: {PHASES.map(p => `wave ${p} ${t.byPhase[p]}`).join(', ')}. By priority: {Object.entries(PRIORITY_LABELS).map(([k, l]) => `${l.toLowerCase()} ${t.byPriority[k]}`).join(', ')}.
      </div>
      {t.problems.length > 0 && (
        <ul className="list-disc pl-4 mt-2 text-[10px] text-red-700">{t.problems.slice(0, 30).map(p => <li key={p}>{p}</li>)}{t.problems.length > 30 && <li>and {t.problems.length - 30} more</li>}</ul>
      )}
    </Question>
  );
}

const ISSUE_COLS = [['desks', 'Move ID or desk', 'text'], ['user_name', 'User or team', 'text'], ['item', 'Item or issue', 'text'], ['qty', 'Qty', 'num'],
  ['asset_tag', 'Asset tag', 'text'], ['from_location', 'From', 'text'], ['to_location', 'To', 'text'], ['owner', 'Owner', 'text'],
  ['status', 'Status', ISSUE_STATUS_LABELS], ['due_date', 'Needed by', 'date'], ['action', 'Action or notes', 'text']];
const RISK_COLS = [['item', 'Risk or dependency', 'text'], ['reason', 'Impact', 'text'], ['action', 'Mitigation or action', 'text'], ['rag', 'RAG', 'rag'],
  ['owner', 'Owner', 'text'], ['due_date', 'Due', 'date'], ['status', 'Status', RISK_STATUS_LABELS]];

export default function DeskMoveSections({ survey, parts, setParts, setField, locked, dayOpen }) {
  return (
    <>
      <Moves parts={parts} setParts={setParts} locked={locked} dayOpen={dayOpen} />
      <Summary parts={parts} />
      <Extras kind="issue" parts={parts} setParts={setParts} locked={locked} cols={ISSUE_COLS} tour="survey-move-issues"
        hint="Non-standard kit, labelled assets, accessibility adjustments and actions that must follow the user." />
      <Extras kind="risk" parts={parts} setParts={setParts} locked={locked} cols={RISK_COLS} title="Desk move risks and early checks" tour="survey-move-risks"
        hint="Anything that could stop a safe, supported move or delay the business being ready." />
      {(dayOpen || survey.move_outcome || survey.evidence_location) && (
        <Question title="Move sign-off" hint="Recorded once the move is done." tour="survey-move-signoff">
          <Field text="Move outcome"><Pick options={MOVE_OUTCOME_LABELS} value={survey.move_outcome} disabled={locked && !dayOpen} onChange={x => setField('move_outcome', x)} /></Field>
          <div className="mt-1.5"><Field text="Evidence or photos stored at"><input className={input} value={survey.evidence_location || ''} disabled={locked && !dayOpen} onChange={e => setField('evidence_location', e.target.value)} /></Field></div>
        </Question>
      )}
    </>
  );
}
